import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Button, Paper, Grid, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Tooltip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, useTheme, CircularProgress,
  List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Edit as EditIcon,
  Delete as DeleteIcon, HealthAndSafety as HealthIcon,
  RestartAlt as RestartIcon, Storage as StorageIcon,
  RocketLaunch as BootstrapIcon, PowerSettingsNew as RebootIcon,
  CheckCircle as OkIcon, Error as FailIcon, Warning as WarnIcon,
  PauseCircle as PauseIcon, PlayCircle as PlayIcon,
  RestartAlt as ResetStatsIcon,
} from '@mui/icons-material';
import {
  fetchDCPNodes, fetchDCPNodeStats, createDCPNode, updateDCPNode,
  checkDCPNodeHealth, deleteDCPNode, restartDCPNodeServices,
  bootstrapDCPNode, rebootDCPNode, provisionDCPNode,
} from '../store/slices/dcpNodesSlice';
import api from '../services/api';

function DCPNodes({ role = 'encode' }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { nodes, error } = useSelector((state) => state.dcpNodes);

  // Filter by role: 'encode' tab shows encode + both nodes;
  // 'utility' tab shows utility + both nodes. role='both' nodes appear in both views.
  const allowedRoles = role === 'utility' ? ['utility', 'both'] : ['encode', 'both'];
  const visibleNodes = (nodes || []).filter(n => allowedRoles.includes(n.role || 'both'));

  // Stats computed client-side from the filtered list — fleet-wide /stats
  // would over-count (e.g. include utility-01 in the encoder Total).
  const stats = {
    total: visibleNodes.length,
    online: visibleNodes.filter(n => n.is_online).length,
    processing: visibleNodes.filter(n => n.current_dcp_job_id).length,
    total_jobs_completed: visibleNodes.reduce((s, n) => s + (n.jobs_completed || 0), 0),
  };
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [bootstrapResult, setBootstrapResult] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(null);
  const [provisionDialog, setProvisionDialog] = useState(null);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionConfig, setProvisionConfig] = useState({
    nas_ip: '192.168.50.10',
    smb_username: '', smb_password: '',
    network_config: '',
    output_smb_share: 'Screeners', output_mountpoint: '/mnt/Screeners', output_subdir: 'DCPOutput',
    input_smb_share: 'Screeners', input_mountpoint: '/mnt/Screeners', input_subdir: 'Source',
  });
  const [formData, setFormData] = useState({
    name: '', hostname: '', ip_address: '', api_port: 8420,
    ssh_user: 'it', ssh_port: 22, ssh_key_path: '/opt/watermarked-screeners/.ssh/dcp_encoder',
  });

  useEffect(() => {
    dispatch(fetchDCPNodes());
    dispatch(fetchDCPNodeStats());
    // Poll while the page is mounted — DCP node state changes are infrequent
    // (health loop is 30s, dispatch is 5s), so 8s polling gives near-real-
    // time UX without WS plumbing.
    const t = setInterval(() => {
      dispatch(fetchDCPNodes());
      dispatch(fetchDCPNodeStats());
    }, 8000);
    return () => clearInterval(t);
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchDCPNodes());
    dispatch(fetchDCPNodeStats());
  };

  const handleAdd = async () => {
    try {
      await dispatch(createDCPNode(formData)).unwrap();
      setAddDialogOpen(false);
      setFormData({
        name: '', hostname: '', ip_address: '', api_port: 8420,
        ssh_user: 'it', ssh_port: 22, ssh_key_path: '/opt/watermarked-screeners/.ssh/dcp_encoder',
      });
    } catch (_err) { /* redux */ }
  };

  const handleEdit = async () => {
    if (!editDialog) return;
    try {
      await dispatch(updateDCPNode({ nodeId: editDialog.id, nodeData: formData })).unwrap();
      setEditDialog(null);
    } catch (_err) { /* redux */ }
  };

  const openEditDialog = (node) => {
    setFormData({
      name: node.name, hostname: node.hostname, ip_address: node.ip_address,
      api_port: node.api_port, ssh_user: node.ssh_user, ssh_port: node.ssh_port,
      ssh_key_path: node.ssh_key_path || '/opt/watermarked-screeners/.ssh/dcp_encoder',
    });
    setEditDialog(node);
  };

  const handleBootstrap = async (nodeId) => {
    setBootstrapping(nodeId);
    setBootstrapResult(null);
    try {
      const result = await dispatch(bootstrapDCPNode(nodeId)).unwrap();
      setBootstrapResult(result);
      handleRefresh();
    } catch (err) {
      setBootstrapResult({ overall: 'failed', message: typeof err === 'string' ? err : 'Bootstrap failed', steps: [] });
    } finally {
      setBootstrapping(null);
    }
  };

  const handleProvision = async () => {
    if (!provisionDialog) return;
    setProvisioning(true);
    try {
      const result = await dispatch(provisionDCPNode({ nodeId: provisionDialog.id, config: provisionConfig })).unwrap();
      setBootstrapResult(result);
      setProvisionDialog(null);
      handleRefresh();
    } catch (err) {
      setBootstrapResult({ overall: 'failed', message: typeof err === 'string' ? err : 'Provision failed', steps: [] });
      setProvisionDialog(null);
    } finally {
      setProvisioning(false);
    }
  };

  const openProvisionDialog = (node) => {
    // Pre-fill network config with the node's known second NIC IP
    const ip = node.ip_address;
    const lastOctet = ip.split('.').pop();
    setProvisionConfig(prev => ({
      ...prev,
      network_config: `ens18:dhcp:|ens19:static:192.168.50.${lastOctet}/24`,
    }));
    setProvisionDialog(node);
  };

  const handleReboot = async (nodeId, nodeName) => {
    if (!window.confirm(`Reboot ${nodeName}? The node will be offline for ~1 minute.`)) return;
    try {
      await dispatch(rebootDCPNode(nodeId)).unwrap();
      handleRefresh();
    } catch (_err) { /* redux */ }
  };

  const totalLabel = role === 'utility' ? 'Total Utility' : 'Total Encoders';
  const statCards = [
    { label: totalLabel, value: stats.total, color: theme.palette.primary.main },
    { label: 'Online', value: stats.online, color: theme.palette.success.main },
    { label: 'Processing', value: stats.processing, color: theme.palette.warning.main },
    { label: 'Jobs Completed', value: stats.total_jobs_completed, color: theme.palette.info.main },
  ];

  const formatDiskFree = (gb) => {
    if (!gb) return '—';
    if (gb > 1000) return `${(gb / 1000).toFixed(1)} TB`;
    return `${gb.toFixed(0)} GB`;
  };

  const stepIcon = (s) => {
    if (s === 'ok') return <OkIcon fontSize="small" color="success" />;
    if (s === 'warning') return <WarnIcon fontSize="small" color="warning" />;
    return <FailIcon fontSize="small" color="error" />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {role === 'utility' ? 'Utility Nodes' : 'DCP Encode Nodes'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={handleRefresh}><RefreshIcon /></IconButton></Tooltip>
          <Tooltip title="Reset jobs_completed / jobs_failed counters across the fleet">
            <IconButton onClick={async () => {
              if (!confirm('Reset job counters on every DCP node?')) return;
              await api.post('/api/dcp-nodes/reset-stats');
              dispatch(fetchDCPNodes());
              dispatch(fetchDCPNodeStats());
            }}>
              <ResetStatsIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
            Add Node
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((card) => (
          <Grid size={{ xs: 6, sm: 3 }} key={card.label}>
            <Paper sx={{ p: 2, textAlign: 'center', borderTop: `3px solid ${card.color}` }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: card.color }}>{card.value}</Typography>
              <Typography variant="body2" color="text.secondary">{card.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Nodes table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>DCP-o-matic</TableCell>
              <TableCell>CPUs</TableCell>
              <TableCell>Disk Free</TableCell>
              <TableCell>Jobs</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleNodes.filter(n => n.is_active).map((node) => (
              <TableRow key={node.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StorageIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight={500}>{node.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{node.ip_address}:{node.api_port}</Typography>
                </TableCell>
                <TableCell>
                  {node.is_paused ? (
                    <Tooltip
                      title={node.current_dcp_job_id
                        ? `Paused — current build ${node.current_dcp_job_id} continues, no new dispatch`
                        : 'Paused — node is idle, dispatcher will skip it'}
                    >
                      <Chip label={node.current_dcp_job_id ? 'Draining' : 'Paused'} color="warning" size="small" />
                    </Tooltip>
                  ) : (
                    <Chip
                      label={node.is_online ? 'Online' : 'Offline'}
                      color={node.is_online ? 'success' : 'default'}
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {node.dcpomatic_installed
                    ? <Chip label="Installed" size="small" color="success" variant="outlined" />
                    : <Chip label="Not Installed" size="small" color="error" variant="outlined" />
                  }
                </TableCell>
                <TableCell>{node.cpu_count || '—'}</TableCell>
                <TableCell>{formatDiskFree(node.disk_free_gb)}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {node.jobs_completed} done / {node.jobs_failed} failed
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Provision (fresh install)">
                    <IconButton size="small" onClick={() => openProvisionDialog(node)}>
                      <AddIcon fontSize="small" color="info" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Bootstrap (verify + fix)">
                    <IconButton size="small" onClick={() => handleBootstrap(node.id)}
                      disabled={bootstrapping === node.id}
                    >
                      {bootstrapping === node.id
                        ? <CircularProgress size={18} />
                        : <BootstrapIcon fontSize="small" color="primary" />
                      }
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Check Health">
                    <IconButton size="small" onClick={() => dispatch(checkDCPNodeHealth(node.id))}>
                      <HealthIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={node.is_paused
                      ? 'Resume — node will accept new builds'
                      : node.current_dcp_job_id
                        ? 'Pause after current build — current build runs to completion, no new dispatch'
                        : 'Pause — dispatcher will skip this node'}
                  >
                    <IconButton
                      size="small"
                      onClick={async () => {
                        await api.put(`/api/dcp-nodes/${node.id}`, { is_paused: !node.is_paused });
                        dispatch(fetchDCPNodes());
                      }}
                      sx={{ color: node.is_paused ? 'success.main' : 'warning.main' }}
                    >
                      {node.is_paused ? <PlayIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Restart Services">
                    <IconButton size="small" onClick={() => dispatch(restartDCPNodeServices(node.id))}>
                      <RestartIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reboot Node">
                    <IconButton size="small" onClick={() => handleReboot(node.id, node.name)}>
                      <RebootIcon fontSize="small" color="warning" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEditDialog(node)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => dispatch(deleteDCPNode(node.id))}>
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {visibleNodes.filter(n => n.is_active).length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No DCP nodes configured. Add one to get started.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit dialog */}
      <Dialog open={addDialogOpen || Boolean(editDialog)} onClose={() => { setAddDialogOpen(false); setEditDialog(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editDialog ? 'Edit DCP Node' : 'Add DCP Node'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Hostname" value={formData.hostname} onChange={(e) => setFormData(f => ({ ...f, hostname: e.target.value }))} fullWidth />
            <TextField label="IP Address" value={formData.ip_address} onChange={(e) => setFormData(f => ({ ...f, ip_address: e.target.value }))} fullWidth />
            <Grid container spacing={2}>
              <Grid size={4}>
                <TextField label="API Port" type="number" value={formData.api_port} onChange={(e) => setFormData(f => ({ ...f, api_port: parseInt(e.target.value) }))} fullWidth />
              </Grid>
              <Grid size={4}>
                <TextField label="SSH User" value={formData.ssh_user} onChange={(e) => setFormData(f => ({ ...f, ssh_user: e.target.value }))} fullWidth />
              </Grid>
              <Grid size={4}>
                <TextField label="SSH Port" type="number" value={formData.ssh_port} onChange={(e) => setFormData(f => ({ ...f, ssh_port: parseInt(e.target.value) }))} fullWidth />
              </Grid>
            </Grid>
            <TextField label="SSH Key Path" value={formData.ssh_key_path} onChange={(e) => setFormData(f => ({ ...f, ssh_key_path: e.target.value }))} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialogOpen(false); setEditDialog(null); }}>Cancel</Button>
          <Button variant="contained" onClick={editDialog ? handleEdit : handleAdd}>
            {editDialog ? 'Save' : 'Add Node'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bootstrap results dialog */}
      <Dialog open={Boolean(bootstrapResult)} onClose={() => setBootstrapResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Bootstrap Results
          {bootstrapResult?.overall === 'ok'
            ? <Chip label="Passed" color="success" size="small" sx={{ ml: 1 }} />
            : <Chip label="Issues Found" color="error" size="small" sx={{ ml: 1 }} />
          }
        </DialogTitle>
        <DialogContent>
          {bootstrapResult?.message && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{bootstrapResult.message}</Typography>
          )}
          <List dense>
            {(bootstrapResult?.steps || []).map((step, i) => (
              <ListItem key={i}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {stepIcon(step.status)}
                </ListItemIcon>
                <ListItemText
                  primary={step.step}
                  secondary={
                    step.error ? step.error :
                    step.version ? step.version :
                    step.mounts ? step.mounts.join(', ') :
                    step.interfaces ? step.interfaces :
                    step.api ? `API: ${step.api}, Encoder: ${step.encoder}` :
                    null
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBootstrapResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Provision dialog */}
      <Dialog open={Boolean(provisionDialog)} onClose={() => !provisioning && setProvisionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Provision Node: {provisionDialog?.name}
          <Typography variant="body2" color="text.secondary">
            Full install on a fresh Debian server — installs packages, DCP-o-matic, configures NAS mounts, and starts services.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="subtitle2">NAS Connection</Typography>
            <Grid container spacing={2}>
              <Grid size={4}>
                <TextField label="NAS IP" value={provisionConfig.nas_ip} fullWidth size="small"
                  onChange={(e) => setProvisionConfig(c => ({ ...c, nas_ip: e.target.value }))} />
              </Grid>
              <Grid size={4}>
                <TextField label="SMB Username" value={provisionConfig.smb_username} fullWidth size="small"
                  onChange={(e) => setProvisionConfig(c => ({ ...c, smb_username: e.target.value }))} />
              </Grid>
              <Grid size={4}>
                <TextField label="SMB Password" type="password" value={provisionConfig.smb_password} fullWidth size="small"
                  onChange={(e) => setProvisionConfig(c => ({ ...c, smb_password: e.target.value }))} />
              </Grid>
            </Grid>

            <Typography variant="subtitle2">Network Interfaces</Typography>
            <TextField
              label="Network Config"
              value={provisionConfig.network_config}
              fullWidth size="small"
              onChange={(e) => setProvisionConfig(c => ({ ...c, network_config: e.target.value }))}
              helperText="Format: ens18:dhcp:|ens19:static:192.168.50.x/24"
            />

            <Typography variant="subtitle2">SMB Shares</Typography>
            <Grid container spacing={2}>
              <Grid size={4}>
                <TextField label="Output Share" value={provisionConfig.output_smb_share} fullWidth size="small"
                  onChange={(e) => setProvisionConfig(c => ({ ...c, output_smb_share: e.target.value }))} />
              </Grid>
              <Grid size={4}>
                <TextField label="Output Mount" value={provisionConfig.output_mountpoint} fullWidth size="small"
                  onChange={(e) => setProvisionConfig(c => ({ ...c, output_mountpoint: e.target.value }))} />
              </Grid>
              <Grid size={4}>
                <TextField label="Output Subdir" value={provisionConfig.output_subdir} fullWidth size="small"
                  onChange={(e) => setProvisionConfig(c => ({ ...c, output_subdir: e.target.value }))} />
              </Grid>
              <Grid size={4}>
                <TextField label="Input Share" value={provisionConfig.input_smb_share} fullWidth size="small"
                  onChange={(e) => setProvisionConfig(c => ({ ...c, input_smb_share: e.target.value }))} />
              </Grid>
              <Grid size={4}>
                <TextField label="Input Mount" value={provisionConfig.input_mountpoint} fullWidth size="small"
                  onChange={(e) => setProvisionConfig(c => ({ ...c, input_mountpoint: e.target.value }))} />
              </Grid>
              <Grid size={4}>
                <TextField label="Input Subdir" value={provisionConfig.input_subdir} fullWidth size="small"
                  onChange={(e) => setProvisionConfig(c => ({ ...c, input_subdir: e.target.value }))} />
              </Grid>
            </Grid>

            {provisioning && (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Provisioning in progress — this may take several minutes...
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProvisionDialog(null)} disabled={provisioning}>Cancel</Button>
          <Button variant="contained" onClick={handleProvision} disabled={provisioning || !provisionConfig.smb_username}>
            {provisioning ? 'Provisioning...' : 'Provision Node'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DCPNodes;
